import type {
  TaskMetadata,
  TaskMetadataCtaConfig,
  TaskMetadataDisplayCtx,
} from 'src/types/task-and-reminder';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {getRedirectPath} from 'src/components/crm/lib/common';
import {TRM_PATHS} from 'src/utils/app-shell';

import {
  Card,
  CardActions,
  CardContent,
  CardFooter,
  CardHeader,
} from '@spaced-out/ui-design-system';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  BodySmall,
  BodySmallBold,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import css from './metadata-details.css';


const useUserCreatedCtaConfig = (
  metadata: TaskMetadata,
): Array<TaskMetadataCtaConfig> => {
  const {t} = useLabelTranslation();
  return React.useMemo(() => {
    if (metadata.cta_config.length > 0) {
      return [];
    }

    const ctas: Array<TaskMetadataCtaConfig> = [];

    if (metadata.candidate_details?.id) {
      const candidateId = encodeURIComponent(metadata.candidate_details.id);
      ctas.push({
        redirect_url: `/crm/candidates?selectedTab=overview&candidateId=${candidateId}&subTab=details`,
        button_label: t('View_Candidate', 'View Candidate'),
        button_icon: 'user',
      });
    }

    if (metadata.job_details?.id) {
      const jobId = encodeURIComponent(metadata.job_details.id);
      ctas.push({
        redirect_url: `/crm/job/${jobId}/info/details`,
        button_label: t('View_Job', 'View Job'),
        button_icon: 'briefcase',
      });
    }

    return ctas;
  }, [metadata, t]);
};

export const MetadataDetails = ({metadata}: {metadata: TaskMetadata}) => {
  const {t} = useLabelTranslation();
  const userCreatedCtas = useUserCreatedCtaConfig(metadata);

  const allCtas =
    metadata.cta_config.length > 0 ? metadata.cta_config : userCreatedCtas;

  const displayEntries = metadata.display_config
    ? metadata.display_config
    : ([] as Array<TaskMetadataDisplayCtx>);
  const hasDisplayCtx = displayEntries.length > 0;

  if (allCtas.length === 0 && !hasDisplayCtx) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <SubTitleSmall color="secondary">
          {t('Task_Details', 'Task Details')}
        </SubTitleSmall>
      </CardHeader>
      <CardContent
        className={hasDisplayCtx ? css.displayContextGrid : undefined}
      >
        {hasDisplayCtx ? (
          displayEntries?.map?.(({label, value}: TaskMetadataDisplayCtx) => (
            <div className={css.displayContextItem} key={label}>
              <BodySmall color="secondary">{label}</BodySmall>
              <BodySmallBold>{value}</BodySmallBold>
            </div>
          ))
        ) : (
          <EmptyState
            title={t('No_task_details_present', 'No task details present')}
            imageVariant="data"
          />
        )}
      </CardContent>
      {allCtas.length > 0 ? (
        <CardFooter>
          <CardActions className={css.ctaActions}>
            <div className={css.ctaGrid}>
              {allCtas.map((cta, index) => (
                <Button
                  className={css.ctaButton}
                  key={cta.redirect_url}
                  type={index % 2 === 0 ? 'secondary' : 'primary'}
                  onClick={() => {
                    if (
                      Object.values(TRM_PATHS).includes(cta.redirect_url as any)
                    ) {
                      window.open(
                        getRedirectPath(cta.redirect_url) ?? '',
                        '_blank',
                      );
                    } else {
                      window.open(cta.redirect_url, '_blank');
                    }
                  }}
                  testId={generateTestId({
                    base: 'task-metadata-cta',
                    index,
                  })}
                >
                  {cta.button_label}
                </Button>
              ))}
            </div>
          </CardActions>
        </CardFooter>
      ) : null}
    </Card>
  );
};
